import PizZipUtils from "pizzip/utils";
import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatInternationalNumber } from "./format";

export type ColumnMapping = Record<string, string>;

/**
 * Generates and downloads an Excel file from JSON data.
 * @param {Array} data - The array of objects to be converted into Excel.
 * @param {Object} columnMapping - An object mapping field names to custom column names.
 * @param {string} sheetName - Name of the Excel sheet.
 * @param {string} fileName - Name of the downloaded Excel file.
 */
export const downloadExcel = (
    data: any,
    columnMapping: ColumnMapping,
    sheetName: string,
    fileName: string,
    autoIndex: boolean = true
) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
        console.error("Invalid or empty data provided for Excel export.");
        return;
    }

    // Map the data dynamically based on the provided column mapping
    const mappedData = data.map((item, index) => {
        let mappedItem: Record<string, string | number> = {};
        if (autoIndex) {
            mappedItem["STT"] = index + 1;
        }
        Object.keys(columnMapping).forEach((key) => {
            const value = key in item ? item[key] : "N/A";
            // Handle null values
            const displayValue = value === null
                ? "N/A"
                : typeof value === "number"
                    ? formatInternationalNumber(value)
                    : value;
            mappedItem[columnMapping[key]] = displayValue;
        });
        return mappedItem;
    });

    // Create a worksheet from the mapped data
    const ws = XLSX.utils.json_to_sheet(mappedData);

    // Create a new workbook and append the worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write the Excel file to a Blob
    const fileData = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    // Create a Blob from the file data
    const blob = new Blob([fileData], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create a link element and trigger the download
    const link = document.createElement("a");
    const url = window.URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", `${fileName}.xlsx`); // Set the file name
    document.body.appendChild(link);
    link.click(); // Trigger the download
    link.remove(); // Clean up the link element after download
    window.URL.revokeObjectURL(url); // Revoke the object URL to release resources
};

function loadFile(url: any, callback: any) {
    PizZipUtils.getBinaryContent(url, callback);
  }


// Returns a DOCX Blob generated from the given template and data (no download side-effect)
export const generateDocumentBlob = (data: any, template: any): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    loadFile(template, function (error: any, content: any) {
      if (error) {
        return reject(error);
      }
      try {
        const zip = new PizZip(content);
        const doc = new Docxtemplater(zip, {
          paragraphLoop: true,
          linebreaks: true,
        });

        // Use new API (avoid deprecated setData/resolveData)
        doc.render(data);

        const out = doc.getZip().generate({
          type: "blob",
          mimeType:
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        });
        resolve(out as Blob);
      } catch (e: any) {
        // Log docxtemplater error details to help debug template issues
        try {
          if (e && e.properties && Array.isArray(e.properties.errors)) {
            console.error("Docxtemplater render errors:", e.properties.errors);
          } else {
            console.error("Docxtemplater render error:", e);
          }
        } catch (_) {
          // ignore logging failure
        }
        reject(e);
      }
    });
  });
};

export const generateDocument = (data: any, template: any, fileName: string) => {
    loadFile(template, function (error: any, content: any) {
      if (error) {
        throw error;
      }
      var zip = new PizZip(content);
      var doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true
      });

      doc.setData(data);

      try {
        doc.render();
      } catch (error: any) {
        console.log(error);
      }

      var out = doc.getZip().generate({
        type: "blob",
        mimeType:
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      });
      saveAs(out, `${fileName}.docx`);
    });
  };
